// Peer of react-leaflet; not listed as a direct dependency.
// eslint-disable-next-line import/no-extraneous-dependencies
import { useLeafletContext } from '@react-leaflet/core';
import L from 'leaflet';
import React, {
  createContext,
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import MarkerClusterGroup from 'react-leaflet-markercluster';

import { createCompositeClusterIcon } from './createCompositeClusterIcon';

type MarkerClusterGroupReady = L.MarkerClusterGroup & {
  // Leaflet.markercluster private fields — present after the group is added to the map
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _map?: L.Map;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _topClusterLevel?: unknown;
};

/**
 * Clustering stays on at every zoom: switching it off leaves markers that share a position stacked
 * on top of each other with no group to fan out. Instead the radius tightens as the user zooms in,
 * so by the deepest levels only icons that genuinely overlap are still grouped.
 */
const maxClusterRadius = (zoom: number): number => {
  if (zoom <= 15) return 80;
  if (zoom === 16) return 55;
  if (zoom === 17) return 35;
  return 15;
};

const ClusterRefreshContext = createContext<() => void>(() => undefined);

export const useClusterRefresh = (): (() => void) => useContext(ClusterRefreshContext);

const RegisterClusterGroup: FC<{
  register: (group: L.MarkerClusterGroup | null) => void;
}> = ({ register }) => {
  const context = useLeafletContext();

  useEffect(() => {
    register(context.layerContainer as L.MarkerClusterGroup);
    return () => register(null);
  }, [context.layerContainer, register]);

  return null;
};

const isClusterReady = (group: L.MarkerClusterGroup | null): group is MarkerClusterGroupReady => {
  if (!group) {
    return false;
  }
  const ready = group as MarkerClusterGroupReady;
  // eslint-disable-next-line no-underscore-dangle -- leaflet.markercluster internals
  return Boolean(ready._map && ready._topClusterLevel);
};

const UnifiedMarkerCluster: FC<{ children: ReactNode }> = ({ children }): ReactElement => {
  const groupRef = useRef<L.MarkerClusterGroup | null>(null);
  const rafRef = useRef<number | null>(null);

  const register = useCallback((group: L.MarkerClusterGroup | null) => {
    groupRef.current = group;
  }, []);

  const refreshClusters = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const group = groupRef.current;
      if (!isClusterReady(group)) {
        return;
      }
      try {
        group.refreshClusters();
      } catch {
        // Cluster group not fully initialized yet (e.g. mid-mount).
      }
    });
  }, []);

  useEffect(
    () => () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    },
    [],
  );

  return (
    <ClusterRefreshContext.Provider value={refreshClusters}>
      <MarkerClusterGroup
        maxClusterRadius={maxClusterRadius}
        spiderfyOnMaxZoom
        showCoverageOnHover={false}
        chunkedLoading
        iconCreateFunction={createCompositeClusterIcon}
      >
        <RegisterClusterGroup register={register} />
        {children}
      </MarkerClusterGroup>
    </ClusterRefreshContext.Provider>
  );
};

export default React.memo(UnifiedMarkerCluster);
